import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createStytchB2BClient, StytchB2BProvider } from '@stytch/react/b2b';
import { BrowserRouter } from 'react-router-dom';
import { App } from './App';
import {
  createLocalDevStytchClient,
  installLocalDevRequestAuth,
  resolveLocalDevSession,
} from './auth/localDevSession';
import { initializeInvitationAnalytics } from './analytics/invitationAnalytics';
import { initializeWebSyncClientInfo } from './collab/webSyncClientInfo';
import { consoleConfig } from './config';
import { initializeTheme } from './theme';
import { installNativeBridge } from './embedded/nativeBridgeApi';
import './styles.css';
/**
 * The shared components' own stylesheet, loaded once at the host rather than by
 * whichever surface happens to mount first. It carries the shared-docs tree row
 * chrome and the tracker element resets, and the docs tree paints before any
 * document surface does — so importing it only from a document component left
 * the first tree render unstyled.
 */
import '@nimbalyst/collab-bundle/styles.css';

initializeWebSyncClientInfo();
initializeInvitationAnalytics();
// Inside the phone app only: exposes `window.__nimbalystConsoleBridge` and posts `ready`.
installNativeBridge();

// Apply the theme before the first paint so the console never flashes light
// while React mounts.
initializeTheme();

// Development-only local sign-in against a loopback collab server; see
// `localDevSession`. `import.meta.env.DEV` is a compile-time constant, so a
// production build keeps only the real Stytch client.
const localDevSession = import.meta.env.DEV
  ? resolveLocalDevSession({
    dev: import.meta.env.DEV,
    pageHostname: window.location.hostname,
    collabApiBaseUrl: consoleConfig.collabApiBaseUrl,
    config: import.meta.env.VITE_LOCAL_SESSION,
  })
  : null;
if (import.meta.env.DEV && localDevSession) {
  installLocalDevRequestAuth(localDevSession, consoleConfig.collabApiBaseUrl);
}

const stytch = import.meta.env.DEV && localDevSession
  ? createLocalDevStytchClient(localDevSession) as unknown as ReturnType<typeof createStytchB2BClient>
  : createStytchB2BClient(consoleConfig.stytchPublicToken);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <StytchB2BProvider stytch={stytch}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </StytchB2BProvider>
  </StrictMode>,
);
